/**
 * RepeatInvoiceDialog — iter 82.4.
 *
 * Duplica un gasto existente N veces (una por mes, mismo día). El backend
 * hace cap de 12 meses; el usuario elige cuántos meses hacia adelante quiere
 * repetir (slider/input 1-12).
 */
import { useState } from "react";
import { toast } from "sonner";
import { Repeat } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";

export default function RepeatInvoiceDialog({
  open,
  onOpenChange,
  invoice,
  lang = "es",
  onDone,
}) {
  const [months, setMonths] = useState(3);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!invoice?.id || busy) return;
    const clamped = Math.max(1, Math.min(12, parseInt(months) || 0));
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/repeat`, {
        months: clamped,
      });
      toast.success(
        lang === "es"
          ? `Duplicado ${data.count} veces`
          : `Duplicated ${data.count} times`,
      );
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent data-testid="repeat-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Repeat size={18} className="text-[var(--primary)]" />
            {lang === "es" ? "Repetir este gasto cada mes" : "Repeat this expense monthly"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            {lang === "es" ? (
              <>
                Se crearán copias de <b>{invoice?.vendor || "este gasto"}</b> el
                mismo día de cada mes, empezando por el mes siguiente al
                original ({invoice?.date}). Máximo <b>12 meses</b>.
              </>
            ) : (
              <>
                Copies of <b>{invoice?.vendor || "this expense"}</b> will be
                created on the same day-of-month, starting the month after
                the original ({invoice?.date}). Max <b>12 months</b>.
              </>
            )}
          </p>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
              {lang === "es" ? "Cuántos meses" : "How many months"}
            </Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                max={12}
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                className="w-24 h-9 font-mono-num"
                data-testid="repeat-invoice-months"
                disabled={busy}
              />
              <input
                type="range"
                min={1}
                max={12}
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                className="flex-1 accent-[var(--primary)]"
                data-testid="repeat-invoice-slider"
                disabled={busy}
              />
              <span className="text-xs text-[var(--text-muted)] font-mono-num min-w-[3ch] text-right">
                {months}×
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="repeat-invoice-cancel-btn"
          >
            {lang === "es" ? "Cancelar" : "Cancel"}
          </Button>
          <Button
            onClick={confirm}
            disabled={busy}
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            data-testid="repeat-invoice-confirm-btn"
          >
            {busy
              ? "..."
              : (lang === "es"
                  ? `Crear ${Math.max(1, Math.min(12, parseInt(months) || 0))} copias`
                  : `Create ${Math.max(1, Math.min(12, parseInt(months) || 0))} copies`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
